import {
  ANCHOR_TYPE,
  AVAILABILITY_MODE,
  AVAILABILITY_MODE_LABELS,
  COMPLETED_BY,
  COMPLETED_BY_OPTIONS,
  EDIT_AFTER_SUBMISSION_MODE,
  END_RULES_COMBINE,
  END_RULE_TYPE,
  PLATFORM_TYPE,
  REPEAT_MODE,
  RESOLVE_TO,
  SAVE_RESUME_MODE,
  formatDurationLabel,
} from '../constants'
import { summarizeRepeat } from './repeatSummary'
import { summarizeAnchor, summarizeCombinedAnchors } from './anchorSummary'
import { formatRelativeToVisitEndRule } from './endRuleSummary'

export const buildScheduleLabel = schedule => {
  if (schedule?.combineAnchors) return 'Combined Anchor'
  const type = schedule?.anchors?.[0]?.type
  if (type === ANCHOR_TYPE.visit || type === ANCHOR_TYPE.visitCycle) return 'Triggered by Visit'
  if (type === ANCHOR_TYPE.participantStartDate) return 'Relative to Participant Start'
  if (type === ANCHOR_TYPE.instrument) return 'Triggered by Instrument'
  return schedule?.name || 'Deployment schedule'
}

/** @deprecated */
export const buildRuleLabel = buildScheduleLabel

const add = (parts, text, emphasize = false) => {
  if (text) parts.push({ text, emphasize })
}

const formatOpenFor = availability => {
  const value = availability?.openFor?.value
  const unit = availability?.openFor?.unit || 'hours'
  if (availability?.mode !== AVAILABILITY_MODE.fixedDuration || !value) return ''
  return `${value} ${unit}`
}

const summarizeEndRules = endRules => {
  const rules = endRules?.rules || []
  if (!rules.length) return ''
  const bits = rules.map(rule => {
    if (rule.type === END_RULE_TYPE.afterNOccurrences) return `${rule.count} occurrences`
    if (rule.type === END_RULE_TYPE.relativeToVisit) {
      return formatRelativeToVisitEndRule(rule)
    }
    return ''
  })
  const combine =
    endRules.combine === END_RULES_COMBINE.earliestOf
      ? 'earliest of'
      : endRules.combine === END_RULES_COMBINE.latestOf
        ? 'latest of'
        : ''
  if (bits.length === 1) return bits[0]
  return `${combine} ${bits.join(' or ')}`
}

const recipientPhrase = completedBy => {
  if (completedBy === COMPLETED_BY.both) return 'Both Subject and Caregiver'
  return COMPLETED_BY_OPTIONS.find(option => option.key === completedBy)?.text || ''
}

const platformPhrase = platformType => {
  if (platformType === PLATFORM_TYPE.web) return 'the web'
  if (platformType === PLATFORM_TYPE.mobile) return 'the mobile app'
  if (platformType === PLATFORM_TYPE.both) return 'the web and mobile app'
  return ''
}

const summarizeAudience = (schedule, ctx = {}) => {
  const recipient = recipientPhrase(schedule.audience?.completedBy)
  const platform = platformPhrase(schedule.audience?.platformType)
  let phrase = ''
  if (recipient && platform) phrase = `${recipient} via ${platform}`
  else if (recipient) phrase = recipient
  else if (platform) phrase = `via ${platform}`
  if (ctx.audience) {
    phrase = phrase ? `${phrase} · ${ctx.audience}` : ctx.audience
  }
  return phrase
}

export const buildPlainLanguageSegments = (schedule, ctx = {}) => {
  const parts = []
  const nameForAnchor = ctx.nameForAnchor
  const trigger = schedule?.combineAnchors
    ? summarizeCombinedAnchors(schedule, nameForAnchor)
    : summarizeAnchor(schedule?.anchors?.[0], nameForAnchor?.(schedule?.anchors?.[0], 0))
  const instrument = ctx.instrumentName || 'this instrument'
  add(parts, 'Deploy ')
  add(parts, instrument, true)
  const audience = summarizeAudience(schedule, ctx)
  if (audience) {
    add(parts, ' to ')
    add(parts, audience, true)
  }
  add(parts, ' ')
  add(parts, trigger, true)

  const availability = schedule?.availability || {}
  const openFor = formatOpenFor(availability)
  if (openFor) {
    add(parts, ', open for ')
    add(parts, openFor, true)
  } else if (availability.mode && availability.mode !== AVAILABILITY_MODE.fixedDuration) {
    add(parts, ', ')
    add(parts, AVAILABILITY_MODE_LABELS[availability.mode]?.toLowerCase() || availability.mode, true)
  }

  const daily = availability.dailyWindow
  if (daily?.enabled && daily.from && daily.until) {
    add(parts, ', visible ')
    add(parts, `${daily.from}–${daily.until}`, true)
    add(parts, ' each day')
  }

  const repeat = summarizeRepeat(schedule?.repeat)
  add(parts, ', ')
  add(parts, repeat, schedule?.repeat?.mode && schedule.repeat.mode !== REPEAT_MODE.doesNotRepeat)

  const endSummary = summarizeEndRules(schedule?.endRules)
  if (endSummary) {
    add(parts, ', series ends at ')
    add(parts, endSummary, true)
  }
  add(parts, '.')
  return parts
}

export const buildPlainLanguageSummary = (schedule, nameForAnchor, ctx) =>
  buildPlainLanguageSegments(schedule, { ...ctx, nameForAnchor })
    .map(part => part.text)
    .join('')

export const buildPlainLanguageFooter = ({ instancePermissions, scheduleIndex, scheduleCount } = {}) => {
  const bits = []
  const edit = instancePermissions?.editAfterSubmission
  const save = instancePermissions?.saveResume
  const saveValue = save?.value ?? save?.minutes
  const editValue = edit?.value ?? edit?.hours
  if (save?.mode === SAVE_RESUME_MODE.yesMinutes && saveValue) {
    bits.push(`Participants can save and resume within ${formatDurationLabel(saveValue, save.unit || 'minutes')}.`)
  }
  if (edit?.mode === EDIT_AFTER_SUBMISSION_MODE.hours && editValue) {
    bits.push(
      `Answers can be edited for ${formatDurationLabel(editValue, edit.unit || 'hours')} after submission.`,
    )
  }
  if (scheduleCount > 1) {
    const others = Array.from({ length: scheduleCount }, (_, index) => index + 1).filter(n => n !== scheduleIndex + 1)
    bits.push(`Schedules ${others.join(' and ')} resolve independently and add their own instances.`)
  }
  return bits.join(' ')
}
